import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
    ArrowLeft, Edit, FileText, Plus, Mail, Phone, Globe, MapPin, Building2,
    TrendingUp, IndianRupee, Clock, User, Tag as TagIcon, AlertCircle,
    CreditCard, Percent, Calendar,
} from 'lucide-react';
import { api, fmtMoney, fmtDate } from '../../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { StatusBadge } from './InvoicingDashboard';

export default function CustomerDetailPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const [c, setC] = useState(null);
    const [statement, setStatement] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const [customer, stmt, invs] = await Promise.all([
                    api.invGetCustomer(id),
                    api.invCustomerStatement(id),
                    api.invListInvoices({ customer_id: id, limit: 100 }),
                ]);
                setC(customer);
                setStatement(stmt);
                setInvoices(invs.items || []);
            } catch (e) {
                toast.error('Failed to load customer');
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading) return <div className="space-y-3"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 w-full" /></div>;
    if (!c) return <div>Customer not found</div>;

    // Derived stats
    const stats = invoices.reduce((acc, i) => {
        acc.total_billed += (i.status !== 'cancelled') ? (i.grand_total || 0) : 0;
        acc.total_paid += (i.amount_paid || 0);
        acc.total_due += (i.status !== 'cancelled') ? (i.amount_due || 0) : 0;
        if (i.status === 'issued' || i.status === 'partial') acc.open_count++;
        if (i.status === 'paid') acc.paid_count++;
        if (i.overdue) acc.overdue_count++;
        return acc;
    }, { total_billed: 0, total_paid: 0, total_due: 0, open_count: 0, paid_count: 0, overdue_count: 0 });

    const avgInvoiceSize = invoices.length ? stats.total_billed / invoices.length : 0;
    const isCreditNearLimit = c.credit_limit && (c.outstanding || 0) >= (c.credit_limit * 0.8);
    const isCreditOverLimit = c.credit_limit && (c.outstanding || 0) > c.credit_limit;

    return (
        <div className="space-y-5" data-testid="customer-detail-page">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => nav('/invoicing/customers')} data-testid="btn-back"><ArrowLeft size={16} /></Button>
                    <div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="text-2xl font-heading font-semibold tracking-tight">{c.display_name || c.name}</h1>
                            {c.tax_treatment && c.tax_treatment !== 'regular' && (
                                <Badge variant="outline" className="text-[10px] uppercase">{c.tax_treatment.replace('_', ' ')}</Badge>
                            )}
                            {c.business_type && <Badge variant="secondary" className="text-[10px] capitalize">{c.business_type}</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                            {c.gstin && <span className="font-mono">GSTIN: {c.gstin}</span>}
                            {c.pan && <span className="font-mono">PAN: {c.pan}</span>}
                            {c.customer_since && <span className="flex items-center gap-1"><Calendar size={11} /> Since {fmtDate(c.customer_since)}</span>}
                        </div>
                        {c.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                                {c.tags.map((t) => <Badge key={t} variant="outline" className="text-[10px]"><TagIcon size={9} className="mr-1" />{t}</Badge>)}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" asChild data-testid="btn-edit"><Link to={`/invoicing/customers/${id}/edit`}><Edit size={14} className="mr-1" /> Edit</Link></Button>
                    <Button size="sm" asChild data-testid="btn-new-invoice"><Link to={`/invoicing/invoices/new?customer=${id}`}><Plus size={14} className="mr-1" /> New invoice</Link></Button>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Outstanding" value={fmtMoney(c.outstanding || 0)} icon={IndianRupee} tone={(c.outstanding > 0) ? 'amber' : 'muted'} sub={`${stats.open_count} open invoice${stats.open_count !== 1 ? 's' : ''}`} testid="stat-outstanding" />
                <StatCard label="Lifetime Revenue" value={fmtMoney(stats.total_billed)} icon={TrendingUp} tone="blue" sub={`${invoices.length} invoices billed`} testid="stat-lifetime" />
                <StatCard label="Avg. Invoice" value={fmtMoney(avgInvoiceSize)} icon={FileText} tone="emerald" sub="Across all invoices" testid="stat-avg" />
                <StatCard label={stats.overdue_count > 0 ? 'Overdue' : 'Payment Terms'} value={stats.overdue_count > 0 ? `${stats.overdue_count}` : `Net ${c.payment_terms_days}`} icon={stats.overdue_count > 0 ? AlertCircle : Clock} tone={stats.overdue_count > 0 ? 'red' : 'slate'} sub={stats.overdue_count > 0 ? 'invoices past due' : 'days credit period'} testid="stat-terms" />
            </div>

            {/* Credit warning */}
            {isCreditOverLimit && (
                <Card className="border-red-300 bg-red-50/60 dark:bg-red-950/20">
                    <CardContent className="pt-4 pb-4 flex items-start gap-3">
                        <AlertCircle className="text-red-600 shrink-0 mt-0.5" size={18} />
                        <div className="text-sm">
                            <div className="font-medium text-red-800 dark:text-red-300">Credit limit exceeded</div>
                            <div className="text-red-700/80 dark:text-red-400/80 text-xs mt-0.5">Outstanding {fmtMoney(c.outstanding)} exceeds credit limit of {fmtMoney(c.credit_limit)}. Consider halting new orders until payment.</div>
                        </div>
                    </CardContent>
                </Card>
            )}
            {!isCreditOverLimit && isCreditNearLimit && (
                <Card className="border-amber-300 bg-amber-50/60 dark:bg-amber-950/20">
                    <CardContent className="pt-4 pb-4 flex items-start gap-3">
                        <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={18} />
                        <div className="text-sm">
                            <div className="font-medium text-amber-800 dark:text-amber-300">Credit usage {Math.round((c.outstanding / c.credit_limit) * 100)}%</div>
                            <div className="text-amber-700/80 dark:text-amber-400/80 text-xs mt-0.5">Approaching credit limit of {fmtMoney(c.credit_limit)}.</div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Contact + Address + Financial info */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card data-testid="contact-card">
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><User size={16} /> Contact</CardTitle></CardHeader>
                    <CardContent className="text-sm space-y-2">
                        {c.primary_contact?.name && (
                            <div className="border-b border-border/40 pb-2 mb-2">
                                <div className="font-medium">{c.primary_contact.name}</div>
                                {c.primary_contact.designation && <div className="text-xs text-muted-foreground">{c.primary_contact.designation}</div>}
                            </div>
                        )}
                        <InfoRow icon={Mail} value={c.email || c.primary_contact?.email || '—'} />
                        <InfoRow icon={Phone} value={c.phone || c.primary_contact?.phone || '—'} />
                        {c.alt_phone && <InfoRow icon={Phone} value={c.alt_phone} label="Alt" />}
                        {c.website && <InfoRow icon={Globe} value={c.website} />}
                        {c.additional_contacts?.length > 0 && (
                            <div className="pt-2 mt-2 border-t border-border/40">
                                <div className="text-[10px] uppercase text-muted-foreground mb-1">More contacts</div>
                                {c.additional_contacts.map((ac, i) => (
                                    <div key={ac.id || `${ac.name || ''}|${ac.phone || ''}|${i}`} className="text-xs py-1">
                                        <span className="font-medium">{ac.name}</span>
                                        {ac.designation && <span className="text-muted-foreground"> · {ac.designation}</span>}
                                        {ac.phone && <div className="text-muted-foreground">{ac.phone}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card data-testid="address-card">
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MapPin size={16} /> Address</CardTitle></CardHeader>
                    <CardContent className="text-sm space-y-3">
                        <div>
                            <div className="text-[10px] uppercase text-muted-foreground mb-1">Billing</div>
                            <div className="text-xs">
                                {[c.billing_address?.line1, c.billing_address?.line2, c.billing_address?.city, c.billing_address?.state, c.billing_address?.pincode, c.billing_address?.country].filter(Boolean).join(', ') || <span className="italic text-muted-foreground">Not set</span>}
                            </div>
                        </div>
                        {c.shipping_address && (
                            <div className="pt-2 border-t border-border/40">
                                <div className="text-[10px] uppercase text-muted-foreground mb-1">Shipping</div>
                                <div className="text-xs">
                                    {[c.shipping_address.line1, c.shipping_address.line2, c.shipping_address.city, c.shipping_address.state, c.shipping_address.pincode].filter(Boolean).join(', ')}
                                </div>
                            </div>
                        )}
                        {c.territory && (
                            <div className="pt-2 border-t border-border/40">
                                <div className="text-[10px] uppercase text-muted-foreground mb-1">Territory</div>
                                <div className="text-xs">{c.territory}</div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card data-testid="financial-card">
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CreditCard size={16} /> Financial</CardTitle></CardHeader>
                    <CardContent className="text-sm space-y-2">
                        <InfoLine label="Payment terms" value={`Net ${c.payment_terms_days} days`} />
                        {c.credit_limit != null && <InfoLine label="Credit limit" value={fmtMoney(c.credit_limit)} />}
                        {c.currency && c.currency !== 'INR' && <InfoLine label="Currency" value={c.currency} />}
                        {c.default_discount_percent != null && <InfoLine label="Default discount" value={`${c.default_discount_percent}%`} icon={Percent} />}
                        {c.tds_applicable && <InfoLine label="TDS" value={`${c.tds_percent || 0}% (${c.tds_section || 'N/A'})`} />}
                        {(c.opening_balance || 0) !== 0 && (
                            <InfoLine label={`Opening ${c.opening_balance_type}`} value={fmtMoney(c.opening_balance)} />
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Notes */}
            {c.notes && (
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Internal Notes</CardTitle></CardHeader>
                    <CardContent><div className="text-sm whitespace-pre-line text-muted-foreground">{c.notes}</div></CardContent>
                </Card>
            )}

            {/* Invoice history */}
            <Card data-testid="invoices-card">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="text-base flex items-center gap-2"><FileText size={16} /> Invoice History</CardTitle>
                        <CardDescription className="text-xs">All invoices for this customer</CardDescription>
                    </div>
                    <Button size="sm" asChild><Link to={`/invoicing/invoices/new?customer=${id}`}><Plus size={14} className="mr-1" /> New</Link></Button>
                </CardHeader>
                <CardContent className="p-0">
                    {invoices.length === 0 ? (
                        <div className="text-center py-8 text-sm text-muted-foreground">
                            <FileText size={32} className="mx-auto mb-2 opacity-40" />
                            <div>No invoices yet</div>
                            <Button variant="link" size="sm" asChild><Link to={`/invoicing/invoices/new?customer=${id}`}>Create the first one →</Link></Button>
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-xs text-muted-foreground border-b border-border/40">
                                <tr>
                                    <th className="text-left p-3">Number</th>
                                    <th className="text-left p-3">Date</th>
                                    <th className="text-left p-3">Due</th>
                                    <th className="text-right p-3">Amount</th>
                                    <th className="text-right p-3">Balance</th>
                                    <th className="text-right p-3">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((inv) => (
                                    <tr key={inv.id} className="border-b border-border/30 last:border-0 hover:bg-muted/40 cursor-pointer" onClick={() => nav(`/invoicing/invoices/${inv.id}`)} data-testid={`cust-inv-${inv.id}`}>
                                        <td className="p-3 font-mono text-xs">{inv.invoice_number || <span className="italic text-muted-foreground">DRAFT</span>}</td>
                                        <td className="p-3 text-xs">{fmtDate(inv.invoice_date)}</td>
                                        <td className="p-3 text-xs">{fmtDate(inv.due_date)}</td>
                                        <td className="p-3 text-right tabular-nums font-medium">{fmtMoney(inv.grand_total)}</td>
                                        <td className="p-3 text-right tabular-nums">{fmtMoney(inv.amount_due)}</td>
                                        <td className="p-3 text-right"><StatusBadge status={inv.status} overdue={inv.overdue} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            {/* Statement of Account */}
            <Card data-testid="statement-card">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">Statement of Account</CardTitle>
                    <CardDescription className="text-xs">Running balance including invoices and payments · Final balance: <span className="font-semibold text-foreground">{fmtMoney(statement?.final_balance || 0)}</span></CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                    {(statement?.entries || []).length === 0 ? (
                        <div className="text-center py-8 text-sm text-muted-foreground">No transactions yet</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-xs text-muted-foreground border-b">
                                <tr>
                                    <th className="text-left p-3">Date</th>
                                    <th className="text-left p-3">Ref</th>
                                    <th className="text-left p-3">Note</th>
                                    <th className="text-right p-3">Debit</th>
                                    <th className="text-right p-3">Credit</th>
                                    <th className="text-right p-3">Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {statement.entries.map((e, i) => (
                                    <tr key={e.id || e.ref || `stmt-${i}`} className="border-b border-border/30 last:border-0" data-testid={`stmt-row-${i}`}>
                                        <td className="p-3 text-xs">{fmtDate(e.date)}</td>
                                        <td className="p-3 font-mono text-xs">{e.ref || '—'}</td>
                                        <td className="p-3 text-xs">{e.note}</td>
                                        <td className="p-3 text-right tabular-nums">{e.debit ? fmtMoney(e.debit) : '—'}</td>
                                        <td className="p-3 text-right tabular-nums text-emerald-700 dark:text-emerald-400">{e.credit ? fmtMoney(e.credit) : '—'}</td>
                                        <td className="p-3 text-right tabular-nums font-medium">{fmtMoney(e.running_balance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

function StatCard({ label, value, icon: Icon, tone, sub, testid }) {
    const bg = {
        blue: 'bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400',
        amber: 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400',
        emerald: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400',
        red: 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400',
        slate: 'bg-slate-100 dark:bg-slate-800/60 text-slate-600',
        muted: 'bg-muted text-muted-foreground',
    }[tone] || 'bg-muted text-muted-foreground';
    return (
        <Card data-testid={testid}>
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <CardDescription className="text-xs">{label}</CardDescription>
                    <div className={`p-1.5 rounded ${bg}`}><Icon size={14} /></div>
                </div>
            </CardHeader>
            <CardContent>
                <div className="text-xl md:text-2xl font-heading font-semibold tabular-nums">{value}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>
            </CardContent>
        </Card>
    );
}

function InfoRow({ icon: Icon, value, label }) {
    if (!value || value === '—') return null;
    return (
        <div className="flex items-center gap-2 text-sm">
            <Icon size={13} className="text-muted-foreground shrink-0" />
            {label && <span className="text-muted-foreground text-xs">{label}:</span>}
            <span className="truncate">{value}</span>
        </div>
    );
}

function InfoLine({ label, value, icon: Icon }) {
    return (
        <div className="flex items-center justify-between text-xs py-1">
            <span className="text-muted-foreground flex items-center gap-1">{Icon && <Icon size={11} />} {label}</span>
            <span className="font-medium">{value}</span>
        </div>
    );
}
